import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Droplets, CheckCircle, Clock, RefreshCw, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * Read-only "today's watering" status panel.
 *
 * Pulls /api/watering/today-status and shows, for every room that is either
 * scheduled today OR was actually watered today:
 *   - what the Grower asked for (scheduled liters)
 *   - whether the Water Tech has completed it
 *   - actual liters logged + when it was completed
 *
 * Visible to Owner, Grower, Growing Supervisor, Farm Manager — so they all
 * see the same source of truth and the "completed by water tech" status no
 * longer disappears for non-owner roles.
 */
const WateringTodayStatus = () => {
  const [rooms, setRooms] = useState([]);
  const [scheduleSet, setScheduleSet] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/watering/today-status`, { headers: auth() });
      setRooms(data.rooms || []);
      setScheduleSet(!!data.schedule_set);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 60000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const formatTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Filter to rooms that are either scheduled today or already watered today
  const interesting = rooms.filter((r) => r.is_scheduled || r.completed);
  const scheduledCount = rooms.filter((r) => r.is_scheduled).length;
  const completedCount = rooms.filter((r) => r.completed).length;
  const pendingCount = rooms.filter((r) => r.is_scheduled && !r.completed).length;

  return (
    <Card className="mb-4 border-blue-200" data-testid="watering-today-status">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Droplets className="w-5 h-5 text-blue-600" />
          Today&apos;s Watering Status
        </CardTitle>
        <div className="flex items-center gap-3">
          <Badge className="bg-blue-100 text-blue-800">{scheduledCount} scheduled</Badge>
          <Badge className="bg-green-100 text-green-800">{completedCount} completed</Badge>
          {pendingCount > 0 && <Badge className="bg-amber-100 text-amber-800">{pendingCount} pending</Badge>}
          <Button size="sm" variant="outline" onClick={fetchStatus} title="Refresh" data-testid="refresh-watering-status">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && interesting.length === 0 && <p className="text-gray-500 text-sm">Loading…</p>}
        {!loading && !scheduleSet && completedCount === 0 && (
          <p className="text-sm text-gray-500">
            No watering schedule set for today and nothing logged yet.
          </p>
        )}
        {interesting.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-gray-200 text-gray-500">
                  <th className="py-2 pr-4">Room</th>
                  <th className="py-2 pr-4">Scheduled</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Completed at</th>
                  <th className="py-2 pr-4">Notes for tech</th>
                </tr>
              </thead>
              <tbody>
                {interesting.map((r) => (
                  <tr key={r.room_id} className="border-b border-gray-100" data-testid={`watering-row-${r.room_number}`}>
                    <td className="py-2 pr-4 font-medium">{r.room_name}</td>
                    <td className="py-2 pr-4">
                      {r.is_scheduled ? (
                        <span><strong>{r.scheduled_liters ?? '—'}</strong> L{r.scheduled_time ? ` @ ${r.scheduled_time}` : ''}</span>
                      ) : (
                        <span className="text-gray-400">not scheduled</span>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      {r.completed ? (
                        <span className="inline-flex items-center gap-1 text-green-700">
                          <CheckCircle className="w-4 h-4" />
                          Completed
                          {r.actual_liters != null && <span className="text-gray-600"> · {r.actual_liters}L given</span>}
                        </span>
                      ) : r.is_scheduled ? (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <Clock className="w-4 h-4" /> Awaiting water tech
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-gray-600">
                          <AlertTriangle className="w-4 h-4" /> Logged without schedule
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-gray-600">{r.completed ? formatTime(r.completed_at) : '—'}</td>
                    <td className="py-2 pr-4 text-gray-600">{r.scheduled_notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WateringTodayStatus;
